import { ReactElement, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import Swiper from '~/ui/components/common/Swiper/Swiper';
import { Link } from 'hah-fe-core/dist/components';
import { getPrices, IPrices } from 'hah-fe-core/dist/utils/price';
import { useRouter } from 'next/router';
import { computedWithDiscount, computePrices } from 'hah-fe-core/dist/utils';
import { CoreContext } from 'hah-fe-core/dist/context';
import { sitewideDiscount } from '~/constants/siteConfig';

// static prices in EUR
const staticPrices = [
  // Pillows category
  {
    maxRegular: 49,
    minRegular: 49,
    discount: sitewideDiscount,
    isPercentage: true,
  },
  // Duvets category
  {
    maxRegular: 74,
    minRegular: 74,
    discount: sitewideDiscount,
    isPercentage: true,
  },
  // Linens category
  {
    maxRegular: 44,
    minRegular: 44,
    discount: sitewideDiscount,
    isPercentage: true,
  },
];

const BeddingSetSection = (): ReactElement => {
  const { t: tHome } = useTranslation('', { keyPrefix: '$pages.$home.$bedding' });
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const [swiperKey, setSwiperKey] = useState(0);

  const {
    data: { currency, promotion },
  } = useContext(CoreContext);

  const prices = computedWithDiscount(staticPrices, promotion?.promo_is_active);
  const staticPrice = computePrices(prices);

  const currentCurrency = currency;
  const { locale } = useRouter();
  const staticItems = [
    {
      name: tHome('pillowsName'),
      image: '/images/homePage/banners/hp-hotel-bedding-pillows-novotel-desktop.webp',
      prices: getPrices(staticPrice[0][currentCurrency], locale),
      link: tHome('pillowsLink'),
      short_description: tHome('pillowsDescription'),
    },
    {
      name: tHome('duvetsName'),
      image: '/images/homePage/banners/hp-hotel-bedding-duvets-novotel-desktop.webp',
      prices: getPrices(staticPrice[1][currentCurrency], locale),
      link: tHome('duvetsLink'),
      short_description: tHome('duvetsDescription'),
    },
    {
      name: tHome('linensName'),
      image: '/images/homePage/banners/hp-hotel-bedding-linens-novotel-desktop.webp',
      prices: getPrices(staticPrice[2][currentCurrency], locale),
      link: tHome('linensLink'),
      short_description: tHome('linensDescription'),
    },
  ];

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleResize = () => {
      setSwiperKey(prevKey => prevKey + 1);
    };

    mediaQuery.addEventListener('change', handleResize);

    return () => {
      mediaQuery.addEventListener('change', handleResize);
    };
  }, []);
  type GuestSlide = {
    name: string;
    image: string;
    prices: IPrices;
    link: string;
    id: string;
    index: number;
    short_description: string;
  };
  const guestSlides = staticItems;
  const renderSlide = (slide: GuestSlide): ReactElement => (
    <Link href={slide.link} noClass data-event-name="bloc_interact" data-bloc-name={slide.name}>
      <div className="hover-btn-animation">
        <img src={slide.image} alt={slide.name} width={267} height={249} loading="lazy" />
        <div className="hover-content desktop-only">
          <span className="btn base-btn btn-main">
            <span>{tButton('shopNow')}</span>
          </span>
        </div>
      </div>
      <h3>{slide.name}</h3>
      {slide.prices.newPrice && (
        <p className="product-price">
          {tHome('from')} {slide.prices.newPrice}
        </p>
      )}
      {slide.short_description && <p className="description">{slide.short_description}</p>}
      <div>
        <button
          type="button"
          className="btn-plain"
          tabIndex={-1}
          aria-label={`${tButton('shopNow')}: ${slide.name}`}
          aria-disabled="false"
        >
          {tButton('shopNow')}
        </button>
      </div>
    </Link>
  );
  return (
    <section id="hp-bedding" className="hp-bedding hp-section mobile-carousel">
      <Link
        href={tHome('beddingLink')}
        data-event-name="bloc_interact"
        data-bloc-name={tHome('heading')}
      >
        <div className="hp-bedding-lifestyle">
          <div className="hover-btn-animation">
            <picture>
              <source srcSet="images/homePage/banners/hp-hotel-bedding-novotel-desktop.webp" />
              <img
                src="images/homePage/banners/hp-hotel-bedding-novotel-desktop.jpg"
                alt={tHome('heading')}
                width={875}
                height={575}
                loading="lazy"
              />
            </picture>
            <div className="hover-content desktop-only">
              <span className="btn base-btn btn-main">
                <span>{tButton('shopNow')}</span>
              </span>
            </div>
          </div>
          <div className="copy-block">
            <p className="eyebrow">{tHome('eyebrow')}</p>
            <h2>{tHome('heading')}</h2>
            <p>{tHome('description')}</p>
            <button className="btn-main" type="button">
              {tButton('shopNow')}
            </button>
          </div>
        </div>
      </Link>
      <div className="hp-content-wrapper common-carousel">
        <Swiper
          key={swiperKey}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          slides={guestSlides}
          arrowBorder
          renderSlide={renderSlide}
          slideClassName={() => ''}
          navigation // Include navigation
          pagination // Include pagination
          containerModifierClass="swiper-container-"
          className="swiper-container swiper-guest swiper-wrapper"
          watchOverflow
          // Default values - making mobile as default
          slidesPerView={1.5}
          /* slidesPerGroup={1} */ // Move 'X' slides at a time
          spaceBetween={30} // Add space between slides
          touchEventsTarget="container"
          breakpoints={{
            // when window width is >= 768px
            768: {
              slidesPerView: 3,
              /* slidesPerGroup: 1, */
              spaceBetween: 25,
              allowTouchMove: false,
            },
          }}
        />
      </div>
    </section>
  );
};

export default BeddingSetSection;
